import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AlertComponent from "../../components/AlertComponent";
import Loading from "../../components/Loading";

export default function GrupoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    nome: "",
    descricao: ""
  });
  const [permissoesSelecionadas, setPermissoesSelecionadas] = useState([]);
  const [modulos, setModulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  // Carrega todas as permissões disponíveis
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API}/permissoes`)
      .then(res => {
        const data = res.data?.data || [];
        setModulos(Array.isArray(data) ? data : []);
      })
      .catch(err => console.error(err))
      .finally(() => {
        if (!isEdit) setLoadingData(false);
      });
  }, [isEdit]);

  // Carrega dados do grupo se for edição
  useEffect(() => {
    if (isEdit) {
      axios.get(`${process.env.REACT_APP_API}/grupos/${id}`)
        .then((res) => {
          const data = res.data?.data || res.data;
          setFormData({
            nome: data.nome || "",
            descricao: data.descricao || ""
          });
          // Extrai os IDs das permissões já associadas
          const permIds = (data.permissoes || []).map(p => p.id);
          setPermissoesSelecionadas(permIds);
        })
        .catch((err) => {
          console.error(err);
          AlertComponent("Erro", "Erro ao carregar grupo", { icon: "error" });
        })
        .finally(() => setLoadingData(false));
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePermissaoToggle = (permissaoId) => {
    setPermissoesSelecionadas(prev => {
      if (prev.includes(permissaoId)) {
        return prev.filter(id => id !== permissaoId);
      } else {
        return [...prev, permissaoId];
      }
    });
  };

  const handleModuloToggle = (modulo) => {
    const permissoesDoModulo = modulo.permissoes.map(p => p.id);
    const todasSelecionadas = permissoesDoModulo.every(id => permissoesSelecionadas.includes(id));

    if (todasSelecionadas) {
      // Remove todas do módulo
      setPermissoesSelecionadas(prev => prev.filter(id => !permissoesDoModulo.includes(id)));
    } else {
      // Adiciona todas do módulo
      setPermissoesSelecionadas(prev => [...new Set([...prev, ...permissoesDoModulo])]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        permissoesIds: permissoesSelecionadas
      };

      if (isEdit) {
        await axios.put(`${process.env.REACT_APP_API}/grupos/${id}`, payload);
        AlertComponent("Sucesso", "Grupo salvo com sucesso!", { icon: "success" });
      } else {
        await axios.post(`${process.env.REACT_APP_API}/grupos`, payload);
      }
      navigate(`/grupos?filtro=${encodeURIComponent(formData.nome)}`);
    } catch (error) {
      console.error("Erro ao salvar grupo:", error);
      AlertComponent("Erro", error?.response?.data?.message ?? "Erro ao salvar grupo", { icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) return <Loading />;

  return (
    <div className="w-full">
      {/* HEADER */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-800">
          {isEdit ? "Editar Grupo" : "Novo Grupo"}
        </h1>
        <p className="text-sm text-slate-500">
          {isEdit
            ? "Atualize as informações do grupo e suas permissões"
            : "Preencha as informações para criar um novo grupo"}
        </p>
      </div>

      {/* FORMULÁRIO */}
      <form onSubmit={handleSubmit}>
        {/* Dados básicos */}
        <div className="bg-white p-8 rounded-xl shadow border mb-6">
          <h2 className="text-lg font-medium text-gray-800 mb-6">Dados do Grupo</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome *
              </label>
              <input
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                placeholder="Digite o nome do grupo"
                required
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição
              </label>
              <input
                type="text"
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-slate-300"
                placeholder="Descrição do grupo (opcional)"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Permissões */}
        <div className="bg-white p-8 rounded-xl shadow border mb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-medium text-gray-800">Permissões</h2>
              <p className="text-sm text-gray-500">
                {permissoesSelecionadas.length} permissão(ões) selecionada(s)
              </p>
            </div>
          </div>

          {modulos.length > 0 ? (
            <div className="space-y-6">
              {modulos.map(modulo => {
                const permissoesDoModulo = modulo.permissoes || [];
                const selecionadasNoModulo = permissoesDoModulo.filter(p => 
                  permissoesSelecionadas.includes(p.id)
                ).length;
                const todasSelecionadas = permissoesDoModulo.length > 0 && 
                  selecionadasNoModulo === permissoesDoModulo.length;
                const algunsSelecionados = selecionadasNoModulo > 0 && !todasSelecionadas;

                return (
                  <div key={modulo.id} className="border rounded-lg overflow-hidden">
                    {/* Header do módulo */}
                    <div 
                      className="flex items-center gap-3 p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleModuloToggle(modulo)}
                    >
                      <input
                        type="checkbox"
                        checked={todasSelecionadas}
                        ref={el => {
                          if (el) el.indeterminate = algunsSelecionados;
                        }}
                        onChange={() => handleModuloToggle(modulo)}
                        className="w-5 h-5 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                        onClick={e => e.stopPropagation()}
                      />
                      <div className="flex-1">
                        <span className="font-medium text-gray-800">{modulo.nome}</span>
                        <span className="text-sm text-gray-500 ml-2">
                          ({selecionadasNoModulo}/{permissoesDoModulo.length})
                        </span>
                      </div>
                    </div>

                    {/* Permissões do módulo */}
                    <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {permissoesDoModulo.map(permissao => (
                        <label
                          key={permissao.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition ${
                            permissoesSelecionadas.includes(permissao.id)
                              ? 'bg-slate-50 border-slate-300'
                              : 'bg-white border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={permissoesSelecionadas.includes(permissao.id)}
                            onChange={() => handlePermissaoToggle(permissao.id)}
                            className="w-4 h-4 rounded border-gray-300 text-slate-600 focus:ring-slate-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-700 truncate">
                              {permissao.nome}
                            </p>
                            <p className="text-xs text-gray-400 truncate">
                              {permissao.codigo}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">Nenhuma permissão disponível</p>
          )}
        </div>

        {/* AÇÕES */}
        <div className="flex justify-end gap-3 border-t pt-6">
          <button
            type="button"
            onClick={() => navigate("/grupos")}
            className="px-5 py-2 rounded-md border hover:bg-gray-50 disabled:opacity-50"
            disabled={loading}
          >
            Cancelar
          </button>

          <button
            type="submit"
            className="px-6 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? "Salvando..." : isEdit ? "Salvar alterações" : "Criar grupo"}
          </button>
        </div>
      </form>
    </div>
  );
}